import React from 'react';
import { useClose } from '@headlessui/react';

import { saveActiveProfileOperatorNote } from 'flow/actions/extension-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionActiveProfileOperatorNote } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import {
    operatorNoteIsOverLimit,
    OPERATOR_NOTE_COUNT_FROM,
    OPERATOR_NOTE_MAX_LENGTH,
} from 'flow/utils/constants/profile-constants';
import CharacterCountInput from '../ui-components/CharacterCountInput';

interface Props {
    extensionKey: ExtensionKey;
}

/**
 * The free-text status shown next to the active profile. Uncontrolled, so a
 * note arriving from Redux after mount lands in the field without clobbering
 * anything already typed.
 */
const StatusInput = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    // Closes the picker and returns focus to its button.
    const close = useClose();

    const operatorNote = useAppSelector((state) =>
        selectExtensionActiveProfileOperatorNote(state, extensionKey),
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // While an input method (Japanese, Chinese) is composing, Escape
        // cancels the composition and Enter commits it. Neither may close the
        // picker or save a half-typed note. Safari sends the key that ends the
        // composition after it has ended, so isComposing is false there and
        // only keyCode 229 tells, deprecated or not.
        const isComposing =
            event.nativeEvent.isComposing || event.keyCode === 229; // NOSONAR
        if (isComposing) {
            event.stopPropagation();
            return;
        }

        // Escape goes on to the picker, which closes. Every other key stays in
        // the field: the app's shortcuts listen on the document and do not
        // skip text fields.
        if (event.key === 'Escape') {
            return;
        }

        event.stopPropagation();

        if (event.key !== 'Enter') {
            return;
        }

        // Saving moves focus to the picker button during this keydown. Without
        // this, the browser would press that button with the same Enter and
        // open the picker again.
        event.preventDefault();

        // A note stored before the limit existed can still be too long. There
        // is no Save button here to disable, so refuse to save and leave the
        // picker open — the counter is red and explains why.
        if (operatorNoteIsOverLimit(event.currentTarget.value)) {
            return;
        }

        dispatch(
            saveActiveProfileOperatorNote(
                extensionKey,
                event.currentTarget.value,
            ),
        );

        close();
    };

    return (
        <CharacterCountInput
            onKeyDown={handleKeyDown}
            id="profile-status"
            className="bg-gray-200"
            placeholder={i18next.t('profilechooser.message')}
            defaultValue={operatorNote}
            maxLength={OPERATOR_NOTE_MAX_LENGTH}
            characterCountFrom={OPERATOR_NOTE_COUNT_FROM}
        />
    );
};

export default StatusInput;
